import { useState } from "react"
import { Button } from "@/components/ui/button"
import { AppointmentDialogFooter } from "../AppointmentDialogFooter"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { extractApiError } from "@/lib/api-errors"
import { CalThrottledNotice } from "@/components/CalThrottledNotice"
import { isCalThrottled } from "@/lib/calThrottled"
import {
  HostRescheduleOutcome,
  useGetHostRescheduleOptionsQuery,
  useRescheduleAsHostMutation,
} from "../../api/rescheduleApi"
import type { HostRescheduleStart } from "../../api/rescheduleApi"
import { BookingRequestStatus } from "../../lib/bookingStatus"
import { isLiveBooking } from "../../lib/dayGrid"
import {
  HOST_ALREADY_BOOKED_CODE,
  doubleBookingFields,
  isHostAlreadyBooked,
  isOverrideReasonValid,
} from "../../lib/doubleBooking"
import {
  SITE_TIME_ZONE,
  longDateLabel,
  minutesToLabel,
  todayInZone,
  zonedDateAndMinutes,
} from "../../lib/siteTime"
import { SLOT_TYPE_LABEL, slotTypeForSlug } from "../../lib/slotTypes"
import { DoubleBookOverrideSection } from "../DoubleBookOverrideSection"
import type { BookingAction, BookingActionContext, BookingActionFormProps } from "./types"
import { MAX_NOTE_LENGTH } from "./types"
import { useReportBusy } from "./useReportBusy"

/** The server's shortest accepted reason, after trimming — see HostRescheduleRules.ReasonMinLength. */
export const MIN_RESCHEDULE_REASON_LENGTH = 3

/**
 * Whether to offer the trigger at all. A cheap first cut from what the dialog already holds — a CMS
 * booking that is Booked, live in Cal, not yet started, and on today's date in the office — so the
 * button is not drawn on every appointment in the calendar. It decides nothing: the form asks the
 * server's options endpoint, which alone says whether this person may move it (host or
 * administrator), and shows its refusal when it says no.
 */
export function mayOfferReschedule(ctx: BookingActionContext, now: Date = new Date()): boolean {
  if (!ctx.requestId || ctx.statusView?.status !== BookingRequestStatus.Booked) return false
  if (!isLiveBooking(ctx.booking) || ctx.hasStarted) return false

  // Pacific, as the server judges it — not the browser's zone, and not UTC.
  return zonedDateAndMinutes(ctx.booking.start, SITE_TIME_ZONE).date === todayInZone(SITE_TIME_ZONE, now)
}

/**
 * One start as the picker shows it, in Pacific time. `start` and `eventTypeSlug` are the server's own,
 * sent back as they came; `value` is the picker's key for the pair.
 */
export interface RescheduleChoice {
  value: string
  start: string
  eventTypeSlug: string
  label: string
  /** The host is already booked then: only an administrator's double-booking override can move it here. */
  hostBooked?: boolean
}

/**
 * "Monday, Oct 5 · 4:00 PM · Private", in the office's time zone whatever the browser's, named by
 * the slot type it would be booked as (Public or Private). `busyStarts` — the server sends them to
 * administrators only — are merged in, in time order, and marked as booked.
 */
export function rescheduleChoices(
  starts: readonly HostRescheduleStart[],
  timeZone: string = SITE_TIME_ZONE,
  busyStarts: readonly HostRescheduleStart[] = [],
): RescheduleChoice[] {
  const label = ({ start, eventTypeSlug }: HostRescheduleStart) => {
    const { date, minutes } = zonedDateAndMinutes(start, timeZone)
    const slotType = slotTypeForSlug(eventTypeSlug)
    const kind = slotType ? ` · ${SLOT_TYPE_LABEL[slotType]}` : ""
    return `${longDateLabel(date)} · ${minutesToLabel(minutes)}${kind}`
  }
  const choice = (start: HostRescheduleStart) => ({
    value: `${start.eventTypeSlug}|${start.start}`,
    start: start.start,
    eventTypeSlug: start.eventTypeSlug,
  })

  return [
    ...starts.map(start => ({ ...choice(start), label: label(start) })),
    ...busyStarts.map(start => ({ ...choice(start), label: `${label(start)} (host booked)`, hostBooked: true })),
  ].sort((a, b) => Date.parse(a.start) - Date.parse(b.start))
}

/** Whether a reason would pass the server's check. The server checks again. */
export function isRescheduleReasonValid(reason: string): boolean {
  const trimmed = reason.trim()
  return trimmed.length >= MIN_RESCHEDULE_REASON_LENGTH && trimmed.length <= MAX_NOTE_LENGTH
}

/**
 * A failed reschedule in words. A host-already-booked refusal is named outright: the server always
 * sends a message with it, but the code is what says for certain that nothing moved. Otherwise the
 * server's own message comes first, because only it knows whether the appointment is still where it
 * was — its 502s say plainly when the household has none.
 */
export function describeRescheduleFailure(error: unknown): string {
  const failure = error as { status?: number | string; data?: unknown }
  if ((failure?.data as { code?: unknown } | undefined)?.code === HOST_ALREADY_BOOKED_CODE) {
    return "This host already has an appointment at that time. It has been left where it was."
  }

  const detail = extractApiError(failure?.data)
  if (detail) return detail
  if (failure?.status === 403) return "Only the appointment's host can reschedule it."
  if (failure?.status === 404) return "This appointment is no longer in the system."

  return "The appointment could not be rescheduled. It has been left where it was."
}

/** A host moving their own appointment to a free public or private time, on the appointment's own day. */
export const RescheduleAction: BookingAction = {
  id: "rescheduling",
  label: "Reschedule",
  triggerVariant: "outline",
  isAvailable: ctx => mayOfferReschedule(ctx) && ctx.rescheduleOptions?.allowed === true,
  Form: RescheduleForm,
}

function RescheduleForm({ ctx, onDone, onClose, onBusyChange }: BookingActionFormProps) {
  const requestId = ctx.requestId ?? ""
  const options = useGetHostRescheduleOptionsQuery(requestId, { skip: !requestId, refetchOnMountOrArgChange: true })
  const [reschedule, rescheduleState] = useRescheduleAsHostMutation()

  const [choiceValue, setChoiceValue] = useState("")
  const [reason, setReason] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [warning, setWarning] = useState<string | null>(null)

  /** Cal throttled the save (503 calThrottled, AB#514): the busy notice instead of `error`. */
  const [throttled, setThrottled] = useState<unknown>(null)

  /** The server said the host is booked at the chosen time (409 hostAlreadyBooked), which the picker did not know. */
  const [serverSaysBooked, setServerSaysBooked] = useState<string | null>(null)
  const [doubleBookReason, setDoubleBookReason] = useState("")

  const busy = rescheduleState.isLoading
  useReportBusy(onBusyChange, busy)

  const allowed = options.data?.allowed === true
  const choices = rescheduleChoices(options.data?.starts ?? [], SITE_TIME_ZONE, options.data?.busyStarts ?? [])
  const chosen = choices.find(choice => choice.value === choiceValue)

  // The double-booking override is appointments:manage's; the server enforces that, and only sends
  // busy times to those who hold it. A host who is not an administrator just sees the refusal.
  const canDoubleBook = ctx.canManage
  const doubleBooking = canDoubleBook && (chosen?.hostBooked === true || serverSaysBooked !== null)

  const canSubmit = allowed && !!chosen && isRescheduleReasonValid(reason) && !busy && !warning
    && (!doubleBooking || isOverrideReasonValid(doubleBookReason))

  function chooseStart(value: string) {
    setChoiceValue(value)
    setServerSaysBooked(null)
    setError(null)
  }

  async function submit() {
    if (!canSubmit || !chosen) return
    setError(null)
    setThrottled(null)

    const result = await reschedule({
      requestId,
      newStart: chosen.start,
      eventTypeSlug: chosen.eventTypeSlug,
      reason: reason.trim(),
      ...doubleBookingFields(doubleBooking, doubleBookReason),
    })

    if ("error" in result) {
      if (canDoubleBook && !doubleBooking && isHostAlreadyBooked(result.error)) {
        setServerSaysBooked(
          extractApiError((result.error as { data?: unknown }).data)
          || "This host already has an appointment at that time.")
        return
      }

      // Nothing moved: Cal turned the request away before it was made. Retried from the notice, by hand.
      if (isCalThrottled(result.error)) {
        setThrottled(result.error)
        return
      }

      setError(describeRescheduleFailure(result.error))
      return
    }

    // Moved. The uid this dialog was opened on is cancelled, so nothing here addresses a live
    // appointment any more — unless there is something staff must read first.
    if (result.data.outcome === HostRescheduleOutcome.Rescheduled && result.data.message) {
      setWarning(result.data.message)
      return
    }

    onClose()
  }

  return (
    <>
      <section>
        {options.isLoading || options.isFetching ? (
          <p className="text-sm text-muted-foreground">Finding free times…</p>
        ) : options.isError && isCalThrottled(options.error) ? (
          <CalThrottledNotice error={options.error} onRetry={() => void options.refetch()} />
        ) : options.isError ? (
          <p role="alert" className="text-sm text-red-700">
            {extractApiError((options.error as { data?: unknown } | undefined)?.data)
              || "Could not find free times. Try again in a moment."}
          </p>
        ) : !allowed ? (
          <p role="alert" className="text-sm text-red-700">
            {options.data?.deniedReason || "This appointment cannot be rescheduled."}
          </p>
        ) : (
          <>
            <Label htmlFor="reschedule-start" className="text-xs font-medium">
              New time <span className="text-muted-foreground">(public or private, Pacific time)</span>
            </Label>
            {choices.length === 0 ? (
              <p className="mt-1.5 text-sm text-muted-foreground">
                There are no free public or private times for this host in the next two weeks.
              </p>
            ) : (
              <Select value={choiceValue} onValueChange={chooseStart} disabled={busy || !!warning}>
                <SelectTrigger id="reschedule-start" className="mt-1.5 w-full">
                  <SelectValue placeholder="Choose a time" />
                </SelectTrigger>
                <SelectContent>
                  {choices.map(choice => (
                    <SelectItem key={choice.value} value={choice.value}>{choice.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Label htmlFor="reschedule-reason" className="mt-3 block text-xs font-medium">
              Reason <span className="text-muted-foreground">(required; internal, never sent to the household)</span>
            </Label>
            <Textarea
              id="reschedule-reason"
              className="mt-1.5"
              rows={2}
              maxLength={MAX_NOTE_LENGTH}
              value={reason}
              disabled={busy || !!warning}
              placeholder="e.g. Host is out this morning"
              onChange={event => setReason(event.target.value)}
            />

            {doubleBooking && (
              <div className="mt-3">
                <DoubleBookOverrideSection
                  id="reschedule-double-book-reason"
                  message={serverSaysBooked ?? "This host already has an appointment at that time."}
                  reason={doubleBookReason}
                  onReasonChange={setDoubleBookReason}
                  disabled={busy || !!warning}
                />
              </div>
            )}

            <p className="mt-3 text-xs text-muted-foreground">
              {ctx.attendeeName ?? "The attendee"} will be told the new time by CMS. Their confirmation
              number still works, but the booking reference changes.
            </p>
          </>
        )}
      </section>

      {throttled ? (
        <CalThrottledNotice error={throttled} onRetry={() => void submit()} />
      ) : error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      {warning && <p role="alert" className="text-sm text-amber-700">{warning}</p>}

      <AppointmentDialogFooter>
        {warning ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button variant="outline" disabled={busy} onClick={onDone}>Cancel</Button>
            {allowed && (
              <Button variant={doubleBooking ? "destructive" : "default"} disabled={!canSubmit} onClick={submit}>
                {busy ? "Rescheduling…" : doubleBooking ? "Double-book anyway" : "Reschedule"}
              </Button>
            )}
          </>
        )}
      </AppointmentDialogFooter>
    </>
  )
}
